import { useEffect, useRef, forwardRef, useCallback } from "react";
import IMask from "imask";
import { parseYearMonthStringToValidDate } from "../../utils/utils";

export const MaskedInputYearMonth = forwardRef(
  (
    {
      value,
      onClick,
      onChange,          // DatePicker wiring
      onClear,
      onBlur,            // optional parent blur
      handleDateChange,  // your "save" with Date
      setKey,
      closeCalendar,     // optional: pass from parent
      questionId,        // optional (only if you want id)
    },
    ref
  ) => {
    const inputRef = useRef(null);
    const maskRef = useRef(null);
    const dirtyRef = useRef(false);

    const getCurrentRawValue = useCallback(() => {
      const el = inputRef.current;
      if (!el) return "";
      return el.value.replace(/_/g, "").trim();
    }, []);

    const commitIfNeeded = useCallback(
      (raw) => {
        if (!dirtyRef.current) return;

        const currentValue = (raw ?? getCurrentRawValue()).trim();

        if (currentValue === "" || currentValue === "____-__") {
          onClear?.();
          dirtyRef.current = false;
          return;
        }

        const parsed = parseYearMonthStringToValidDate(currentValue);

        if (parsed) {
          handleDateChange(parsed);
        } else {
          onClear?.();
        }

        dirtyRef.current = false;
      },
      [getCurrentRawValue, handleDateChange, onClear]
    );

    const handleKeyDown = (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        commitIfNeeded(getCurrentRawValue());
        setKey?.((k) => k + 1);
        return;
      }

      if (e.key === "Tab") {
        closeCalendar?.();
        commitIfNeeded(getCurrentRawValue());
        // do not preventDefault
      }
    };

    const handleLocalChange = (e) => {
      dirtyRef.current = true;
      onChange?.(e);
    };

    const handleLocalBlur = (e) => {
      commitIfNeeded(e.target.value);
      onBlur?.(e);
    };

    useEffect(() => {
      if (!inputRef.current) return;

      maskRef.current = IMask(inputRef.current, {
        mask: "0000-00",
        lazy: false,
        placeholderChar: "_",
      });

      maskRef.current.on("accept", () => {
        dirtyRef.current = true;
        const event = new Event("input", { bubbles: true });
        inputRef.current.dispatchEvent(event);
      });

      return () => {
        maskRef.current?.destroy();
        maskRef.current = null;
      };
    }, []);

    // Keep mask display in sync with value coming from parent/DatePicker
    useEffect(() => {
      const el = inputRef.current;
      if (!el) return;

      // If user is actively typing, don't override.
      if (document.activeElement === el) return;

      // Value may be ISO like "2026-01-01T00:00:00" -> display "2026-01"
      const display = value ? String(value).slice(0, 7) : "";

      if (maskRef.current) {
        maskRef.current.value = display;
      } else {
        el.value = display;
      }

      dirtyRef.current = false;
    }, [value]);

    // Click outside -> commit
    useEffect(() => {
      const onDocumentMouseDown = (ev) => {
        const el = inputRef.current;
        if (!el) return;

        if (ev.target instanceof Node && !el.contains(ev.target)) {
          closeCalendar?.();

          // If focused, blur will commit
          if (document.activeElement === el) {
            el.blur();
          } else {
            // safety net (edge cases)
            commitIfNeeded();
          }
        }
      };

      document.addEventListener("mousedown", onDocumentMouseDown, true);
      return () => {
        document.removeEventListener("mousedown", onDocumentMouseDown, true);
      };
    }, [closeCalendar, commitIfNeeded]);

    return (
      <input
        ref={(el) => {
          inputRef.current = el;
          if (typeof ref === "function") ref(el);
          else if (ref) ref.current = el;
        }}
        type="text"
        className="form-control"
        id={questionId ? `QUESTION_${questionId}` : undefined}
        placeholder="YYYY-MM"
        defaultValue=""  // value is driven by DatePicker; we sync via effect above
        onClick={onClick}
        onChange={handleLocalChange}
        onBlur={handleLocalBlur}
        onKeyDown={handleKeyDown}
      />
    );
  }
);
